import {
  ComponentBlock,
  InstanceBlock,
  ListBlock,
  isCommit,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import * as React from 'react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '~/sotano-kit/primitives/context-menu'
import { getBlockMenuActions } from '../../../block-wrapper-actions/actions'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useIsGuestInHisBranch, useUserRole } from '~/hooks-data/use-user-role'
import { useEditorStore } from '../../../../editor-store'
import { useRepoStore } from '../../../../repo-store'
import { EditorMode, resolveEditorMode } from '../../../../editor-mode-provider'
import { useTabsStore, useTabsStoreRoot } from '../../../../tabs/store'
import { useMutateWithErrorHandling } from '~/replicache/frontend/use-mutate-with-error-handling'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useToggleState } from '~/hooks/use-toggle-state'
import { isPublicRepo } from '@basehub/data/replicache/mutators/_public-types'
import { clsx } from 'clsx'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import { getId } from '@basehub/utils/id/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { DeleteBlocksParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import {
  useSelectedBranch,
  useBranchOrCommitSegment,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { ReferenceInputStoreContext } from '../../../reference/input'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'
import { useSelectedVariants } from '~/hooks/use-selected-variants'

type TableContextMenuProps = {
  children: React.ReactNode
  selectedRows: (InstanceBlock | ComponentBlock)[]
  parentBlock: ListBlock
  path: string
  disabled?: boolean
  duplicateCallback?: () => void
}

export const TableContextMenu = React.forwardRef<
  HTMLDivElement,
  // renderIf is not meant to change, it's just a way to conditionally render the component. Consider using disabled instead.
  TableContextMenuProps & { renderIf?: boolean }
>(({ renderIf, ...rest }, ref) => {
  if (!renderIf) return rest.children

  return <TableContextMenuInner {...rest} ref={ref} />
})

export const TableContextMenuInner = React.forwardRef<
  HTMLDivElement,
  TableContextMenuProps
>(
  (
    { children, selectedRows, path, disabled, parentBlock, duplicateCallback },
    ref
  ) => {
    const {
      data,
      repoReplicache: replicache,
      isLoading: isLoadingSelectedRepo,
    } = useSelectedTeamAndRepo()
    const repo = data?.repo
    const collapsePropertiesPanel = useUserPreferences(
      (s) => s.collapsePropertiesPanel
    )
    const userId = useUserPreferences((s) => s.id)
    const { teamSlug, repoSlug, isLoading } = useDashboardSlugs()
    const activeTabKey = useTabsStore((s) => s.activeTab?.key)
    const { encodedSegment } = useBranchOrCommitSegment()
    const undoManager = useRepoUndoManager()
    const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
    const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
    const setFocus = useRepoStore((s) => s.setFocus)
    const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)

    // resolve editorMode in place
    const role = useUserRole()
    const editMode = useUserPreferences((s) => s.editMode)
    const branch = useSelectedBranch().branch ?? null
    const selectedBranchOrCommit = useSelectedBranchOrCommitQuery().data
    const playgroundId = usePlaygroundCookie()
    const isPlayground = Boolean(
      !!playgroundId && (!!branch?.playgroundId || branch?.isDefault)
    )
    const isCommitCheckedOut = Boolean(
      !!selectedBranchOrCommit && isCommit(selectedBranchOrCommit)
    )
    const isGuestInHisBranch = useIsGuestInHisBranch()
    const selectedVariants = useSelectedVariants({ parentBlock })

    const editorMode = React.useMemo(() => {
      let mode: EditorMode = 'write:all'
      for (const block of selectedRows) {
        // each result in the loop must be return the last editorMode or anything below that
        // that will ensure that at the end of the loop we'll have the most restrictive mode for the selected blocks
        mode = resolveEditorMode(mode, {
          block,
          parentBlock,
          role,
          isGuestInHisBranch,
          isCommitCheckedOut,
          branch,
          editMode,
          treeManager: treeManagerRef.current,
          isPlayground,
          selectedDashboardVariants: selectedVariants,
        }).editorMode
      }
      return mode
    }, [
      isPlayground,
      selectedRows,
      parentBlock,
      role,
      isGuestInHisBranch,
      isCommitCheckedOut,
      branch,
      editMode,
      treeManagerRef,
      selectedVariants,
    ])

    const tabsStore = useTabsStoreRoot()
    const rootId = useWorkingTreeStore((s) => s.treeRef.current?.root.value.id)
    const sourceBlockId = idToSourceBlockId(parentBlock)
    const refreshOp = React.useMemo(
      () =>
        ({
          op: 'customWindowEvent',
          args: {
            name: `${sourceBlockId}-ur-custom-event`,
            detail: { refresh: true },
          },
        }) satisfies RepoUndoOps[number],
      [sourceBlockId]
    )
    const referenceStore = React.useContext(ReferenceInputStoreContext)
    const handleRemoveReferenceTarget =
      referenceStore?.getState().onRemoveTarget ?? null

    const focusOp = React.useMemo(() => {
      return {
        op: 'focus',
        args: {
          focus: {
            blockId: sourceBlockId,
            type: 'block:wrapper',
          },
          preferredTabKey: activeTabKey,
        },
      } satisfies RepoUndoOps[number]
    }, [activeTabKey, sourceBlockId])

    const { mutateAsync: handleDelete } = useMutateWithErrorHandling(
      async (mutate, ids: string[]) => {
        if (!rootId) return
        const params: DeleteBlocksParams = {
          blockIds: ids,
          rootId,
          idSeed: getId(),
          isoNow: new Date().toISOString(),
        }
        const result = await mutate.deleteBlocks(params)
        if (result.undo) {
          undoManager.current?.push({
            redo: [{ op: 'deleteBlocks', args: params }, refreshOp],
            undo: [
              { op: 'blockBatchMutations', args: { mutations: result.undo } },
              refreshOp,
            ],
          })
        }
        return result
      }
    )
    const toggleState = useToggleState()
    const block = selectedRows[0]

    const actions = React.useMemo(() => {
      const block = selectedRows[0]

      if (
        !repoSlug ||
        !teamSlug ||
        isLoading ||
        isLoadingSelectedRepo ||
        !block
      ) {
        return []
      }

      const normalizedPath = normalizeStringPath({
        path: path + '/' + block.id,
        edgeId: block.id,
        includeEdge: false,
      })
      const parentBlockId = normalizedPath[normalizedPath.length - 1]
      if (!parentBlockId) return []

      if (isPublicRepo(repo) && !isGuestInHisBranch && !isPlayground) {
        return getBlockMenuActions({
          treeManagerRef,
          selectedVariants,
          configureAccess: false,
          blocks: selectedRows,
          repoSlug,
          teamSlug,
          branchOrCommitSegment: encodedSegment,
          parentBlockId,
          repoStore: { setFocus },
          parentBlock,
          tabsStore,
          undoManager: undoManager.current,
        })
      }

      if (
        !replicache ||
        collapsePropertiesPanel === undefined ||
        userId === undefined
      ) {
        return []
      }
      return getBlockMenuActions({
        treeManagerRef,
        selectedVariants,
        blocks: selectedRows,
        path,
        replicache,
        collapsePropertiesPanel,
        userId,
        repoSlug,
        teamSlug,
        branchOrCommitSegment: encodedSegment,
        parentBlockId,
        editorStore: {
          setSelectedBlockId,
          setIsRenamingBlockId,
        },
        editorMode,
        configureAccess: true,
        repoStore: { setFocus },
        parentBlock,
        undoManager: undoManager.current,
        tabsStore,
        urFocusOpStart: focusOp,
        urFocusOpEnd: refreshOp,
        handleDelete: async () => {
          const blockIdsToDelete = selectedRows.map((block) => block.id)
          await handleDelete(blockIdsToDelete)
        },
        handleRemoveReferenceTarget: async () => {
          await handleRemoveReferenceTarget?.(idToSourceBlockId(block))
        },
        duplicateCallback,
        handleRichTextConvertToPlainText: toggleState.handleOn,
      })
    }, [
      selectedVariants,
      selectedRows,
      repoSlug,
      teamSlug,
      isLoading,
      isLoadingSelectedRepo,
      path,
      repo,
      isGuestInHisBranch,
      isPlayground,
      replicache,
      collapsePropertiesPanel,
      userId,
      editorMode,
      treeManagerRef,
      encodedSegment,
      setSelectedBlockId,
      setIsRenamingBlockId,
      setFocus,
      parentBlock,
      undoManager,
      tabsStore,
      focusOp,
      refreshOp,
      duplicateCallback,
      toggleState.handleOn,
      handleDelete,
      handleRemoveReferenceTarget,
    ])

    return (
      <ContextMenu>
        <ContextMenuTrigger disabled={disabled}>{children}</ContextMenuTrigger>
        <ContextMenuContent
          ref={ref}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
          }}
        >
          <ContextMenuLabel className="text-ellipsis whitespace-nowrap overflow-hidden">
            <span className="text-dark-muted">
              {editorMode === 'write:all' && 'Editing '}
            </span>
            {selectedRows.length === 1
              ? selectedRows?.[0]?.title || 'Untitled'
              : `${selectedRows.length} rows`}
          </ContextMenuLabel>

          {actions.map((action) => {
            if (action.key === 'rename' || action.key === 'expand-properties') {
              return null
            }

            switch (action.type) {
              case 'action':
                return (
                  <ContextMenuItem
                    className="space-x-2 text-xs leading-4"
                    key={action.key}
                    disabled={action.disabled}
                    onSelect={() => {
                      if (!block) return
                      setSelectedBlockId(idToSourceBlockId(block))
                      setTimeout(() => action.onSelect())
                    }}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </ContextMenuItem>
                )
              case 'action-link':
                return (
                  <ButtonLink
                    unstyled
                    focusStyled
                    key={action.key}
                    onClick={action.onSelect}
                    href={action.href}
                  >
                    <ContextMenuItem
                      className="space-x-2 text-xs leading-4"
                      disabled={action.disabled}
                    >
                      {action.icon}
                      <span>{action.label}</span>
                    </ContextMenuItem>
                  </ButtonLink>
                )

              case 'separator':
                return <ContextMenuSeparator key={action.key} />

              case 'submenu':
                return (
                  <ContextMenuSub key={action.key}>
                    <ContextMenuSubTrigger asChild disabled={action.disabled}>
                      <span className="space-x-2 text-xs leading-4">
                        {action.icon}
                        <span>{action.label}</span>
                        <ChevronRightIcon
                          width={12}
                          height={12}
                          className="text-dark-muted !ml-auto"
                        />
                      </span>
                    </ContextMenuSubTrigger>

                    <ContextMenuPortal>
                      <ContextMenuSubContent>
                        {action.items.map((sub) => {
                          if (sub.type === 'separator') {
                            return <ContextMenuSeparator key={sub.key} />
                          }

                          if (sub.type !== 'action') {
                            throw new Error('Invalid action type')
                          }

                          return (
                            <ContextMenuItem
                              key={sub.key}
                              className={clsx(
                                'space-x-2 text-xs leading-4',
                                sub.intent === 'danger' && 'text-dark-error'
                              )}
                              onSelect={sub.onSelect}
                              onClick={sub.onClick}
                            >
                              {sub.icon}
                              <span>{sub.label}</span>
                            </ContextMenuItem>
                          )
                        })}
                      </ContextMenuSubContent>
                    </ContextMenuPortal>
                  </ContextMenuSub>
                )

              default:
                console.error({ action })
                throw new Error('Unknown action type')
            }
          })}
        </ContextMenuContent>
      </ContextMenu>
    )
  }
)
